import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  lazy,
  Suspense,
} from 'react';
// Redux
import { useDispatch, useSelector } from 'react-redux';

// Material
import Grid from '@material-ui/core/Grid';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Box from '@material-ui/core/Box';

// CSVLink
import { CSVLink } from 'react-csv';

// Helpers
import { oneWeekAgo, formatDateAndTime } from 'helpers/date';
import { getComparator, stableSort } from 'helpers/tableSortLabels';
import { currencyFormat } from 'helpers/currencyFormat';

// Actions
import { listCompanyReportRequest } from 'store/ducks/company/actions';
import * as ReportActions from 'store/ducks/reports/actions';
import { setOpen, setClose } from 'store/ducks/dialogs/actions';

// Types
import { ApplicationState } from 'store';
import { CompanyReportInterface } from 'store/ducks/company/types';

// Contants
import { REPORTS_TYPES } from 'constants/report';
import ITEMS from 'constants/itemsByFilter';
import TITLES_OPTIONS from 'constants/infoModulesTitle';

// Components
import { XlsHeaderInterface, XlsRowInterface } from 'components';
import { TabPanel } from 'components/TabPanel';

// Styles
import useStyles from './CompanyAcquisitionsReportDatails.styles';

// Lazy Components
const Filter = lazy(() => import('components/Filter'));
const TotalCountComponent = lazy(() =>
  import('components/TotalCountComponent'),
);
const MainContent = lazy(() =>
  import('components').then((mod) => ({ default: mod.MainContent })),
);
const DialogueExport = lazy(() =>
  import('components').then((mod) => ({ default: mod.DialogueExport })),
);
const SearchComponent = lazy(() =>
  import('components').then((mod) => ({ default: mod.SearchComponent })),
);
const ReportTitle = lazy(() =>
  import('components').then((mod) => ({ default: mod.ReportTitle })),
);
const TablePlaceholder = lazy(() =>
  import('components/TablePlaceholder').then((mod) => ({
    default: mod.TablePlaceholder,
  })),
);
const Submissions = lazy(() =>
  import('../../../../components').then((mod) => ({
    default: mod.Submissions,
  })),
);
const History = lazy(() =>
  import('../../../../components').then((mod) => ({ default: mod.History })),
);
const DetailedCompanyAcquisitions = lazy(() =>
  import('./components/DetailedCompanyAcquisitions').then((mod) => ({
    default: mod.DetailedCompanyAcquisitions,
  })),
);
interface CompanyAcquisitionsReportDatailsProps {
  setSubmissionData: (param: any) => void;
  setIsAddSubmissiion: (param: any) => void;
  setIsEditSubmissiion: (param: any) => void;
}

const CompanyAcquisitionsReportDatails: React.FC<CompanyAcquisitionsReportDatailsProps> = ({
  setSubmissionData,
  setIsAddSubmissiion,
  setIsEditSubmissiion,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const csvRef = useRef(null);

  const [tableCSV, setTableCSV] = useState('');
  const [reportIsVisible, setReportIsVisible] = useState(false);
  const [tableReference, setTableReference] = useState();
  const [generalNavGuide, setGeneralNavGuide] = useState(0);
  const [companyList, setCompanyList] = useState<CompanyReportInterface[]>();
  const [xlsHeader, setXlsHeader] = useState<XlsHeaderInterface[]>();
  const [xlsRows, setXlsRows] = useState<Array<XlsRowInterface[]>>([]);
  const { oneDayAgo: startDate, endDate } = oneWeekAgo;

  const [periodString, setPeriodString] = useState('');

  const [simplePdfData, setSimplePdfData] = useState<{
    title: string;
    subtitle: string;
    filters: string;
    headers: any;
    rows: any;
  }>({
    title: '',
    subtitle: '',
    filters: '',
    headers: [],
    rows: [],
  });

  const initialCompanyReportList = useSelector(
    (state: ApplicationState) => state.companyReport.companyReportList,
  );

  const transportOperatorSelected = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorSelected,
  );

  const transportOperatorsList = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorsList,
  );

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setGeneralNavGuide(newValue);
  };

  const handleLoadData = useCallback(() => {
    setReportIsVisible(true);
    const beginningOfTheYear = new Date(new Date().getFullYear(), 0, 1);
    dispatch(
      ReportActions.setSelectedStartDate({
        selectedStartDate: beginningOfTheYear,
      }),
    );

    dispatch(
      listCompanyReportRequest({
        startDate: beginningOfTheYear.setHours(0, 0, 0),
        endDate: new Date().setHours(23, 59, 59),
      }),
    );

    const tempPeriodString = `Dia: ${new Date().toLocaleDateString('pt-BR', {
      dateStyle: 'short',
    })}`;

    setPeriodString(tempPeriodString);
  }, [dispatch, startDate, endDate]);

  const handleApply = useCallback(
    (data: any) => {
      dispatch(setClose({}));
      setReportIsVisible(true);

      const {
        startDate,
        endDate,
        startedAt,
        selectedLines,
        selectedCompanies,
      } = data;

      const isPeriod = startDate?.getTime() < endDate?.getTime();
      let startDateValue;
      let endDateValue;
      if (isPeriod) {
        startDateValue = new Date(startDate).getTime();
        endDateValue = new Date(endDate).getTime();
      } else if (startedAt) {
        startDateValue = new Date(startedAt).setHours(0, 0, 0);
        endDateValue = new Date(startedAt).setHours(23, 59, 59);
      }
      if (isPeriod) {
        dispatch(
          ReportActions.setSelectedStartDate({
            selectedStartDate: startDate,
          }),
        );

        dispatch(
          ReportActions.setSelectedEndDate({
            selectedEndDate: endDate,
          }),
        );
      }

      dispatch(
        listCompanyReportRequest({
          startDate: startDateValue,
          endDate: endDateValue,
          lineIds:
            selectedLines?.length > 0
              ? selectedLines
                  .map((e: { id: string; name: string }) => e.id)
                  .join()
              : undefined,
          companyid:
            selectedCompanies?.length > 0
              ? selectedCompanies.map((c: any) => c.id)
              : undefined,
        }),
      );

      const tempPeriodString = `De: ${
        isPeriod
          ? formatDateAndTime(new Date(startDate))
          : formatDateAndTime(new Date(startedAt))
      } até: ${
        isPeriod
          ? formatDateAndTime(new Date(endDate))
          : formatDateAndTime(new Date(startedAt))
      }`;

      const updatedPeriodString = tempPeriodString;

      setPeriodString(updatedPeriodString);
    },
    [dispatch],
  );

  // generating the table csv
  useEffect(() => {
    let csv = '';
    if (
      Array.isArray(initialCompanyReportList) &&
      initialCompanyReportList.length > 0
    ) {
      let header = `Empresa;JAN;FEV;MAR;ABR;MAI;JUN;JUL;AGO;SET;OUT;NOV;DEZ`;
      if (transportOperatorSelected.type === 1) {
        header += ';Operação\n';
      } else {
        header += '\n';
      }
      csv += header;
      csv += initialCompanyReportList
        .map(
          (company) =>
            `${company.name};${currencyFormat({
              value: Number(company?.months?.['1']) || 0,
            })};${currencyFormat({
              value: Number(company?.months?.['2']) || 0,
            })};${currencyFormat({
              value: Number(company?.months?.['3']) || 0,
            })};${currencyFormat({
              value: Number(company?.months?.['4']) || 0,
            })};${currencyFormat({
              value: Number(company?.months?.['5']) || 0,
            })};${currencyFormat({
              value: Number(company?.months?.['6']) || 0,
            })};${currencyFormat({
              value: Number(company?.months?.['7']) || 0,
            })};${currencyFormat({
              value: Number(company?.months?.['8']) || 0,
            })};${currencyFormat({
              value: Number(company?.months?.['9']) || 0,
            })};${currencyFormat({
              value: Number(company?.months?.['10']) || 0,
            })};${currencyFormat({
              value: Number(company?.months?.['11']) || 0,
            })};${currencyFormat({
              value: Number(company?.months?.['12']) || 0,
            })}${
              transportOperatorSelected.type === 1
                ? ';' +
                    transportOperatorsList.find(
                      (e) => e.id === company?.transportOperatorId,
                    )?.name +
                    '\n' || '---'
                : '\n'
            }`,
        )
        .join('');
      setTableCSV(csv);
    }
  }, [initialCompanyReportList]);

  useEffect(() => {
    const headerSimplePdf = [
      'Empresa',
      'JAN',
      'FEV',
      'MAR',
      'ABR',
      'MAI',
      'JUN',
      'JUL',
      'AGO',
      'SET',
      'OUT',
      'NOV',
      'DEZ',
    ];

    const rowsSimplePdf =
      initialCompanyReportList?.length > 0
        ? initialCompanyReportList?.map((item: any) => [
            item?.name ? item?.name : '-',
            currencyFormat({
              value: Number(item?.months?.['1']) || 0,
            }),
            currencyFormat({
              value: Number(item?.months?.['2']) || 0,
            }),
            currencyFormat({
              value: Number(item?.months?.['3']) || 0,
            }),
            currencyFormat({
              value: Number(item?.months?.['4']) || 0,
            }),
            currencyFormat({
              value: Number(item?.months?.['5']) || 0,
            }),
            currencyFormat({
              value: Number(item?.months?.['6']) || 0,
            }),
            currencyFormat({
              value: Number(item?.months?.['7']) || 0,
            }),
            currencyFormat({
              value: Number(item?.months?.['8']) || 0,
            }),
            currencyFormat({
              value: Number(item?.months?.['9']) || 0,
            }),
            currencyFormat({
              value: Number(item?.months?.['10']) || 0,
            }),
            currencyFormat({
              value: Number(item?.months?.['11']) || 0,
            }),
            currencyFormat({
              value: Number(item?.months?.['12']) || 0,
            }),
          ])
        : [];

    const tempSimplePdfData = {
      title: 'ONBOARD',
      subtitle: 'Relatório de Compras',
      filters: periodString || '',
      headers: headerSimplePdf,
      rows: rowsSimplePdf,
    };

    setSimplePdfData(tempSimplePdfData);
  }, [initialCompanyReportList, periodString]);

  useEffect(() => {
    const staticXlsheader = [
      {
        header: 'Empresa',
        key: 'company',
        width: 20,
      },
      {
        header: 'JAN',
        key: 'JAN',
        width: 20,
      },
      {
        header: 'FEV',
        key: 'FEV',
        width: 20,
      },
      {
        header: 'MAR',
        key: 'MAR',
        width: 20,
      },
      {
        header: 'ABR',
        key: 'ABR',
        width: 20,
      },
      {
        header: 'MAI',
        key: 'MAI',
        width: 20,
      },
      {
        header: 'JUN',
        key: 'JUN',
        width: 20,
      },
      {
        header: 'JUL',
        key: 'JUL',
        width: 20,
      },
      {
        header: 'AGO',
        key: 'AGO',
        width: 20,
      },
      {
        header: 'SET',
        key: 'SET',
        width: 20,
      },
      {
        header: 'OUT',
        key: 'OUT',
        width: 20,
      },
      {
        header: 'NOV',
        key: 'NOV',
        width: 20,
      },
      {
        header: 'DEZ',
        key: 'DEZ',
        width: 20,
      },
    ];

    if (transportOperatorSelected.type === 1) {
      staticXlsheader.push({
        header: 'Operação',
        key: 'operator',
        width: 20,
      });
    }
    setXlsHeader(staticXlsheader);

    const rows = stableSort(
      initialCompanyReportList,
      getComparator('desc', 'createdAt'),
    ).map((data) => {
      const staticXlsRows = [
        {
          key: 'company',
          value: `${data?.name ? data?.name : '-'}`,
          image: null,
        },
        {
          key: 'JAN',
          value: `${currencyFormat({
            value: Number(data?.months?.['1']) || 0,
          })}`,
          image: null,
        },
        {
          key: 'FEV',
          value: `${currencyFormat({
            value: Number(data?.months?.['2']) || 0,
          })}`,
          image: null,
        },
        {
          key: 'MAR',
          value: `${currencyFormat({
            value: Number(data?.months?.['3']) || 0,
          })}`,
          image: null,
        },
        {
          key: 'ABR',
          value: `${currencyFormat({
            value: Number(data?.months?.['4']) || 0,
          })}`,
          image: null,
        },
        {
          key: 'MAI',
          value: `${currencyFormat({
            value: Number(data?.months?.['5']) || 0,
          })}`,
          image: null,
        },
        {
          key: 'JUN',
          value: `${currencyFormat({
            value: Number(data?.months?.['6']) || 0,
          })}`,
          image: null,
        },
        {
          key: 'JUL',
          value: `${currencyFormat({
            value: Number(data?.months?.['7']) || 0,
          })}`,
          image: null,
        },
        {
          key: 'AGO',
          value: `${currencyFormat({
            value: Number(data?.months?.['8']) || 0,
          })}`,
          image: null,
        },
        {
          key: 'SET',
          value: `${currencyFormat({
            value: Number(data?.months?.['9']) || 0,
          })}`,
          image: null,
        },
        {
          key: 'OUT',
          value: `${currencyFormat({
            value: Number(data?.months?.['10']) || 0,
          })}`,
          image: null,
        },
        {
          key: 'NOV',
          value: `${currencyFormat({
            value: Number(data?.months?.['11']) || 0,
          })}`,
          image: null,
        },
        {
          key: 'DEZ',
          value: `${currencyFormat({
            value: Number(data?.months?.['12']) || 0,
          })}`,
          image: null,
        },
      ];

      if (transportOperatorSelected.type === 1) {
        staticXlsRows.push({
          key: 'operator',
          value: `${transportOperatorsList.find(
            (e) => e.id === data?.transportOperatorId,
          )?.name || '---'}`,
          image: null,
        });
      }
      return staticXlsRows;
    }) as XlsRowInterface[][];
    setXlsRows(rows);
  }, [initialCompanyReportList]);

  return (
    <div className={classes.root}>
      <Suspense fallback={'Carregando...'}>
        <MainContent className={classes.paperRoot}>
          <Grid
            container
            spacing={2}
            ref={(ref: any) => setTableReference(ref)}>
            <Grid item xs={12}>
              <Grid
                container
                justifyContent='space-between'
                className={classes.gridContainer}>
                <Grid item>
                  <Suspense fallback={'Carregando...'}>
                    <ReportTitle
                      titleVariant='h4'
                      title='Relatórios de Empresas • Compras'
                      tooltipTitle={
                        TITLES_OPTIONS.REPORTS.COMPANYS.SHOPPING.VALUE
                      }
                    />
                  </Suspense>
                  {/* <Typography
                  variant='h4'
                  color='secondary'
                  data-testid='relatorio_label'>
                  Relatórios de Empresas • Compras
                </Typography> */}
                </Grid>
                <Grid item className={classes.icons}>
                  <Grid item>
                    <CSVLink
                      filename='tabela_relatorio_de_compras_detalhado.csv'
                      data={tableCSV}
                      ref={csvRef}>
                      <Tooltip title='Baixar CSV' placement='top'>
                        <IconButton />
                      </Tooltip>
                    </CSVLink>
                  </Grid>
                </Grid>
              </Grid>
              <Grid container direction='column'>
                <Grid item xs={12} className={classes.scopeFilter}>
                  <Box
                    display='flex'
                    alignItems='center'
                    width='100%'
                    marginTop='8px'
                    className={classes.contentFilter}>
                    <SearchComponent
                      disabledInput={!reportIsVisible}
                      data={initialCompanyReportList}
                      itemsByFilter={ITEMS.ReportOnboardSales}
                      results={(data: any[]) => setCompanyList(data)}
                      modalFilter={() => {
                        dispatch(
                          setOpen({
                            open: true,
                            content: (
                              <Suspense fallback={<p>Carregando...</p>}>
                                <Filter
                                  notSetInitalDate
                                  fields={ITEMS.companyAcquisitions.shopping}
                                  handleApply={handleApply}
                                />
                              </Suspense>
                            ),
                            maxWidth: 'xl',
                          }),
                        );
                      }}
                    />
                    {/* <Grid container justifyContent='flex-end'>
                    <Button
                      className={classes.addButton}
                      onClick={() => setIsAddSubmissiion(true)}>
                      <Typography style={{ marginRight: 8, fontSize: 20 }}>
                        +
                      </Typography>{' '}
                      <Typography style={{ fontSize: 12 }}>
                        Programar Novo
                      </Typography>
                    </Button>
                  </Grid> */}
                  </Box>
                  <Tabs
                    indicatorColor='primary'
                    onChange={handleChange}
                    style={{
                      background: '#ffffff',
                      marginTop: 10,
                    }}
                    textColor='primary'
                    value={generalNavGuide}>
                    <Tab label='Relatório' />
                    <Tab label='Envios' />
                    <Tab label='Histórico' />
                  </Tabs>
                  <TabPanel index={0} value={generalNavGuide}>
                    <Grid item xs={12}>
                      {!reportIsVisible && (
                        <Suspense fallback={'Carregando...'}>
                          <TablePlaceholder onClick={handleLoadData} />
                        </Suspense>
                      )}

                      {reportIsVisible && (
                        <>
                          <Grid className={classes.contentDilogueExport}>
                            <Box
                              display='flex'
                              justifyContent='flex-end'
                              alignItems='center'>
                              {initialCompanyReportList?.length! > 1 && (
                                <Box
                                  width='100%'
                                  display='flex'
                                  justifyContent='flex-end'
                                  mr={2}>
                                  <Suspense fallback='Carregando...'>
                                    <TotalCountComponent
                                      count={
                                        initialCompanyReportList?.length - 1 ||
                                        0
                                      }
                                      title='empresas'
                                      position='right'
                                    />
                                  </Suspense>
                                </Box>
                              )}

                              <Suspense fallback={'Carregando...'}>
                                <DialogueExport
                                  tableInstance={tableReference}
                                  csvRef={csvRef}
                                  simplePdf
                                  simplePdfData={simplePdfData}
                                  columnsPerPage={5}
                                  pdfName='relatorio_de_empresas_compras'
                                  xlsTitle='relatorio_de_empresas_compras'
                                  xlsHeader={xlsHeader}
                                  xlsRows={xlsRows}
                                  pdf
                                  csv
                                  xls
                                />
                              </Suspense>
                            </Box>
                          </Grid>

                          <Suspense fallback={'Carregando...'}>
                            <DetailedCompanyAcquisitions
                              companyList={companyList}
                            />
                          </Suspense>
                        </>
                      )}
                    </Grid>
                  </TabPanel>
                  <TabPanel index={1} value={generalNavGuide}>
                    <Suspense fallback={'Carregando...'}>
                      <Submissions
                        setSubmissionData={setSubmissionData}
                        setIsEditSubmissiion={setIsEditSubmissiion}
                        reportType={REPORTS_TYPES.CHARGE_MONTH_REPORT}
                      />
                    </Suspense>
                  </TabPanel>{' '}
                  <TabPanel index={2} value={generalNavGuide}>
                    <Suspense fallback={'Carregando...'}>
                      <History reportType={REPORTS_TYPES.CHARGE_MONTH_REPORT} />
                    </Suspense>
                  </TabPanel>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </MainContent>
      </Suspense>
    </div>
  );
};

export default CompanyAcquisitionsReportDatails;
export { CompanyAcquisitionsReportDatails };
